import { gql } from '@provision/graphql-client';
import { QueryVariables, Records } from 'src/graphql/types';
import type { StartedSmsParticipation } from './sms-session-context';

export interface ConversationSmsSessionRecord {
  conversationSmsSessionId: string;
  conversationId: string;
  conversationChannelId: string;
  state: string | null;
  openedAt: string | null;
  externalId: string | null;
}

export interface GetConversationSmsSessionQuery {
  getConversationSmsSession: Records<ConversationSmsSessionRecord>;
}

export const GET_OPEN_SMS_SESSION_QUERY = gql`
  query GetOpenSmsSession($params: QueryParams) {
    getConversationSmsSession(queryParams: $params) {
      records {
        conversationSmsSessionId
        conversationId
        conversationChannelId
        state
        openedAt
        externalId
      }
    }
  }
`;

export function getOpenSmsSessionQueryVariables(conversationId: string): QueryVariables {
  return {
    params: {
      filters: [
        { attribute: 'conversationId', operator: '=', value: conversationId },
        { attribute: 'state', operator: '=', value: 'open' },
      ],
    },
  };
}

export const END_SMS_SESSION_MUTATION = gql`
  mutation EndSmsSession($input: EndSmsSessionInput!) {
    endSmsSession(input: $input) {
      conversationSmsSession {
        conversationSmsSessionId
        state
        endedAt
      }
    }
  }
`;

export interface EndSmsSessionMutationResult {
  endSmsSession: {
    conversationSmsSession: {
      conversationSmsSessionId: string;
      state: string;
      endedAt: string | null;
    };
  };
}

export interface EndSmsSessionMutationVars {
  input: { conversationSmsSessionId: string };
}

export const START_SMS_SESSION_MUTATION = gql`
  mutation StartSmsSession($input: StartSmsSessionInput!) {
    startSmsSession(input: $input) {
      conversationSmsSession {
        conversationSmsSessionId
        conversationChannelId
        state
        openedAt
        externalId
      }
      smsParticipation {
        conversationParticipationId
        conversationChannelId
        externalIdentifier
        internalIdentifier
      }
    }
  }
`;

export interface StartSmsSessionMutationResult {
  startSmsSession: {
    conversationSmsSession: {
      conversationSmsSessionId: string;
      conversationChannelId: string;
      state: string;
      openedAt: string | null;
      externalId: string | null;
    };
    smsParticipation: StartedSmsParticipation | null;
  };
}

export interface StartSmsSessionMutationVars {
  input: { conversationId: string; conversationChannelId?: string };
}

/**
 * Result is not read from the response: the channel UPDATE emits an entity event that refreshes
 * the cached participations, and the visibility hook re-derives from those.
 */
export const CHECK_SMS_DELIVERABILITY_MUTATION = gql`
  mutation CheckSmsDeliverability($input: CheckSmsDeliverabilityInput!) {
    checkSmsDeliverability(input: $input) {
      lineType
      lineTypeCheckedAt
      sendable
    }
  }
`;

export interface CheckSmsDeliverabilityMutationVars {
  input: { conversationChannelId: string };
}
